<template>
  <div
    class="tw-space-y-4"
    :class="[
      Classes.FILTERS,
      modal ? 'tw-w-full tw-p-4 tw-pt-12' : 'tw-w-64',
      modal ? 'tw-block' : show ? 'tw-hidden sm:tw-block' : 'tw-hidden',
      { 'tw-order-1': Settings.filterRightSide },
    ]"
  >
    <div v-if="Settings.showSortTabs" class="tw-overflow-visible">
      <base-select
        :model-value="sort.sortBy"
        :options="
          sortOptions.map((x) => ({
            text: $t(x.name, { ugc: ugcName(null, true, false).value }),
            value: x.key,
          }))
        "
        @input="handleSort"
      >
        <template #prepend>
          <font-awesome-icon class="tw-ml-4 tw--mr-1" icon="sort-amount-down" />
        </template>
      </base-select>
    </div>
    <search-box
      v-if="Settings.allowSearching"
      :id="id"
      :placeholder="
        `${$t('search')} ${$t('name').toLowerCase()}` +
        ` ${guideBrowseRoute ? '/ ' + $t('summary').toLowerCase() : ''}`
      "
      full-width
      no-margin
      :max="80"
      @update="$emit('update', 'search')"
    />

    <search-box
      v-if="!userRoute && Settings.allowSearching"
      :id="id"
      full-width
      user-search
      class="tw-my-4"
      :placeholder="`${$t('search')} ${$t(
        guideBrowseRoute ? 'author' : 'creator'
      ).toLowerCase()}`"
      no-margin
      :max="80"
      @update="$emit('update', 'search')"
    />

    <div class="tw-relative">
      <div class="tw-flex tw-justify-end tw-h-6">
        <base-button
          small
          text-link
          :disabled="!search && !searchUserName && !additionalFiltersSelected"
          @click="reset()"
        >
          {{ $t('clear_all') }}
        </base-button>
      </div>
      <div v-for="(f, i) in filter" :key="f.name">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
          <p class="tw-text-sm tw-uppercase tw-tracking-[2px]">
            {{ getTagOptionName(f) }}
          </p>

          <button
            v-if="f.allTags?.length > f.tags?.length"
            class="mod-link !tw-ml-auto"
            @click="toggleShowMore(f.id)"
          >
            {{ f.showMore ? $t('hide_more_tags') : $t('show_more_tags') }}
          </button>
        </div>

        <component
          :is="f.type === 'dropdown' ? 'DropdownFilter' : 'CheckboxFilter'"
          :filter="f"
          wrap-class="tw-flex tw-flex-col tw-gap-y-1"
          @update="(option) => updateFilter(option)"
        />

        <div
          v-if="i < filter.length - 1"
          class="tw-border-t tw-border-[var(--tile-text)] tw-opacity-30 tw-my-4"
        />
      </div>
    </div>
  </div>
</template>

<script>
import DropdownFilter from '@components/List/DropdownFilter.vue'
import CheckboxFilter from '@components/List/CheckboxFilter.vue'
import { getTagOptionName } from '@helpers/localizedTags.js'
import SearchBox from '@components/List/SearchBox.vue'
import Classes from '@shared/js/config/css.js'
import { Settings } from '@config/game.js'
import { computed, toRefs } from 'vue'
import {
  USER_COLLECTION_BROWSE_ROUTE,
  USER_GUIDE_BROWSE_ROUTE,
  USER_MOD_BROWSE_ROUTE,
  GUIDE_BROWSE_ROUTE,
  CONTENT_ROUTE,
  USER_ROUTE,
} from '@config/routeNames.js'
import {
  useResourceValue,
  usePagination,
  useFilter,
  useSearch,
  useRoute,
  useSort,
} from '@composables'

export default {
  components: {
    DropdownFilter,
    CheckboxFilter,
    SearchBox,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    show: {
      type: Boolean,
      required: true,
    },
    sortOptions: {
      type: Array,
      default: () => [],
    },
    tagOptions: {
      type: Array,
      default: null,
    },
    inputLoading: {
      type: Boolean,
      default: false,
    },
    modal: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  setup(props, { emit }) {
    const { id, sortOptions } = toRefs(props)

    const { ugcName } = useResourceValue()
    const { routeName } = useRoute()

    const { filter, settingsTags, updateFilter, resetFilter } = useFilter(
      id.value
    )
    const { sort } = useSort(id.value)
    const { resetPagination } = usePagination(id.value)
    const { clearUserSearch, searchUserName, clearSearch, search } = useSearch(
      id.value
    )

    const additionalFiltersSelected = computed(() =>
      filter.value.some((f) =>
        [...f.selected, ...(f.excluded || [])].some(
          (t) => !settingsTags.value.includes(t.toLowerCase())
        )
      )
    )

    const userRoute = computed(() =>
      [
        USER_COLLECTION_BROWSE_ROUTE,
        USER_GUIDE_BROWSE_ROUTE,
        USER_MOD_BROWSE_ROUTE,
        CONTENT_ROUTE,
        USER_ROUTE,
      ].includes(routeName.value)
    )

    const guideBrowseRoute = computed(
      () => routeName.value === GUIDE_BROWSE_ROUTE
    )

    function reset() {
      resetPagination()
      resetFilter()
      clearSearch()
      clearUserSearch()
      emit('update', 'search')
    }

    function handleSort(input) {
      if (input === sort.value?.sortBy?.value) return

      const selected = sortOptions.value.find((s) => s.key === input)
      if (selected) {
        emit('update', 'sort', { ...selected, dropdown: true })
      }
    }

    function toggleShowMore(id) {
      updateFilter({ id, showMore: true })
    }

    return {
      additionalFiltersSelected,
      getTagOptionName,
      guideBrowseRoute,
      toggleShowMore,
      searchUserName,
      updateFilter,
      handleSort,
      userRoute,
      Settings,
      ugcName,
      Classes,
      search,
      filter,
      reset,
      sort,
    }
  },
}
</script>
